// Pure query-key helpers with no imports and no side effects, so a test that mocks `api` or
// `api/methods` never has to reproduce them and a module can build keys at load time.

/**
 * Substitutes all path variables from provided args, and return the resulting
 * path and remaining args
 */
export const derefPath = (p: string, args: Record<string, unknown> = {}) =>
  Object.entries(args).reduce<[string, Record<string, unknown>]>(
    ([path, rest], [k, v]) =>
      path.includes(`{${k}}`)
        ? [path.replace(`{${k}}`, `${v}`), rest]
        : [path, { ...rest, [k]: v }],
    [p, {}],
  );

/**
 * The key the orval client produces for the same request: the resolved `/api/v1` URL, plus the
 * remaining query params when there are any. Legacy `.key()` / `.keyed()` emit it too, so both
 * layers share one cache entry per request and a file can move to orval without silently
 * detaching from the invalidations its neighbours issue. `legacyKeyShape.test.ts` proves the
 * equality against every GET in swagger.json.
 */
export const queryKeyFor = (path: string, args: Record<string, unknown> | undefined) => {
  const [url, params] = derefPath(`/api/v1${path}`, args ?? {});
  return Object.keys(params).length > 0 ? ([url, params] as const) : ([url] as const);
};

/**
 * Key for a paginated `useInfiniteQuery` over a GET. Its `{ pages, pageParams }` entry must not
 * share an exact key with a plain query of the same request, while a refresh of the plain key
 * still reaches it as a prefix.
 */
export const infiniteQueryKey = (key: readonly unknown[]) => [...key, "infinite"] as const;

const escapeRegExp = (value: string) => value.replace(/[.*+?^$()|[\]\\]/g, "\\$&");

/** Path params that were given match their value; the rest match any single path segment. */
export const toTemplateMatcher = (path: string, known: Record<string, unknown> = {}) =>
  new RegExp(
    `^/api/v1${escapeRegExp(path).replace(/\{([^}]+)\}/g, (_, name: string) =>
      name in known ? escapeRegExp(String(known[name])) : "[^/]+",
    )}$`,
  );

/**
 * Query filters matching every endpoint under a path prefix — `pathPrefixFilters("/retailproducts")`
 * is every retail-product read. A bare `["/retailproducts"]` key cannot express this: the first key
 * element is a whole resolved URL, so a prefix only ever matches whole elements.
 */
export const pathPrefixFilters = (prefix: string) => {
  const base = `/api/v1${prefix}`;
  return {
    predicate: (query: { queryKey: readonly unknown[] }) => {
      const [url] = query.queryKey;
      return typeof url === "string" && (url === base || url.startsWith(`${base}/`));
    },
  };
};
